import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../config/api';
import './RegistrationDetailPage.css';

export const RegistrationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    loadRegistration();
  }, [id]);

  const loadRegistration = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/exchange/admin/registrations/${id}/`);
      setRegistration(data.registration);
      setActivityLogs(data.activity_logs || []);
    } catch (error) {
      console.error('Failed to load registration:', error);
      alert('Kayıt yüklenemedi');
      navigate('/admin/registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (createAccounts = true, adminNotes = '') => {
    try {
      await api.post(`/api/exchange/admin/registrations/${id}/approve/`, {
        create_accounts: createAccounts,
        admin_notes: adminNotes
      });
      setShowApproveModal(false);
      await loadRegistration();
      alert('Kayıt başarıyla onaylandı!');
    } catch (error) {
      console.error('Approval failed:', error);
      alert(error.response?.data?.error || 'Onaylama başarısız');
    }
  };

  const handleReject = async (reason) => {
    try {
      await api.post(`/api/exchange/admin/registrations/${id}/reject/`, {
        reason
      });
      setShowRejectModal(false);
      await loadRegistration();
      alert('Kayıt reddedildi');
    } catch (error) {
      console.error('Rejection failed:', error);
      alert(error.response?.data?.error || 'Reddetme başarısız');
    }
  };

  const handleSchedule = async (scheduledDate, meetingLink, adminNotes = '') => {
    try {
      await api.patch(`/api/exchange/admin/registrations/${id}/schedule/`, {
        scheduled_date: scheduledDate,
        meeting_link: meetingLink,
        admin_notes: adminNotes
      });
      setShowScheduleModal(false);
      await loadRegistration();
      alert('Randevu başarıyla planlandı!');
    } catch (error) {
      console.error('Scheduling failed:', error);
      alert(error.response?.data?.error || 'Randevu planlama başarısız');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { class: 'badge-warning', icon: '⏳', text: 'Onay Bekliyor' },
      'approved': { class: 'badge-info', icon: '✅', text: 'Onaylandı' },
      'rejected': { class: 'badge-danger', icon: '❌', text: 'Reddedildi' },
      'admin_created': { class: 'badge-primary', icon: '👤', text: 'Hesap Oluşturuldu' },
      'appointment_scheduled': { class: 'badge-success', icon: '📅', text: 'Randevu Planlandı' },
      'setup_in_progress': { class: 'badge-info', icon: '⚙️', text: 'Kurulum Devam Ediyor' },
      'setup_completed': { class: 'badge-success', icon: '🎉', text: 'Tamamlandı' },
      'cancelled': { class: 'badge-secondary', icon: '🚫', text: 'İptal Edildi' },
    };
    const badge = badges[status] || { class: 'badge-secondary', icon: '❓', text: status };
    return (
      <span className={`status-badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="detail-page">
        <div className="loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="detail-page">
        <div className="error-state">Kayıt bulunamadı</div>
      </div>
    );
  }

  return (
    <div className="detail-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/admin/registrations')}>
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Geri
        </button>
        <div className="header-info">
          <h1>{registration.organization_name}</h1>
          <div className="header-meta">
            {getStatusBadge(registration.status)}
            <span className="separator">•</span>
            <span className="date">Kayıt: {formatDate(registration.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-bar">
        {registration.status === 'pending' && (
          <>
            <button className="btn-approve" onClick={() => setShowApproveModal(true)}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Onayla
            </button>
            <button className="btn-reject" onClick={() => setShowRejectModal(true)}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Reddet
            </button>
          </>
        )}
        {(registration.status === 'approved' || registration.status === 'admin_created') && (
          <button className="btn-schedule" onClick={() => setShowScheduleModal(true)}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            Randevu Planla
          </button>
        )}
      </div>

      <div className="content-grid">
        {/* Main Info */}
        <div className="main-column">
          {/* Organization Info */}
          <div className="info-card">
            <h2>🏢 Organizasyon Bilgileri</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Organizasyon Adı</label>
                <span>{registration.organization_name}</span>
              </div>
              <div className="info-item">
                <label>Email Domain</label>
                <span>{registration.sender_domain}</span>
              </div>
              <div className="info-item">
                <label>Subdomain</label>
                <span>{registration.subdomain || 'Otomatik oluşturulacak'}</span>
              </div>
              <div className="info-item">
                <label>Şirket Büyüklüğü</label>
                <span>{registration.company_size}</span>
              </div>
              <div className="info-item">
                <label>Sektör</label>
                <span>{registration.industry}</span>
              </div>
              <div className="info-item">
                <label>Ülke</label>
                <span>{registration.country}</span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="info-card">
            <h2>👤 İletişim Bilgileri</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>İletişim Kişisi</label>
                <span>{registration.contact_person_name}</span>
              </div>
              <div className="info-item">
                <label>Email</label>
                <a href={`mailto:${registration.contact_email}`}>{registration.contact_email}</a>
              </div>
              <div className="info-item">
                <label>Telefon</label>
                <a href={`tel:${registration.contact_phone}`}>{registration.contact_phone}</a>
              </div>
            </div>
          </div>

          {/* Admin Account Info */}
          <div className="info-card">
            <h2>🔑 Admin Hesap Bilgileri</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Ad Soyad</label>
                <span>{registration.admin_first_name} {registration.admin_last_name}</span>
              </div>
              <div className="info-item">
                <label>Email</label>
                <a href={`mailto:${registration.admin_email}`}>{registration.admin_email}</a>
              </div>
              {registration.admin_user && (
                <div className="info-item full-width">
                  <label>Hesap Durumu</label>
                  <span className="badge-success">✅ Oluşturuldu</span>
                </div>
              )}
            </div>
          </div>

          {/* Appointment Info */}
          <div className="info-card">
            <h2>📅 Randevu Bilgileri</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Tercih 1</label>
                <span>{formatDate(registration.preferred_date_1)}</span>
              </div>
              <div className="info-item">
                <label>Tercih 2</label>
                <span>{formatDate(registration.preferred_date_2)}</span>
              </div>
              <div className="info-item">
                <label>Tercih 3</label>
                <span>{formatDate(registration.preferred_date_3)}</span>
              </div>
              {registration.scheduled_date && (
                <>
                  <div className="info-item full-width highlight">
                    <label>✅ Planlanan Randevu</label>
                    <span>{formatDate(registration.scheduled_date)}</span>
                  </div>
                  {registration.meeting_link && (
                    <div className="info-item full-width">
                      <label>Toplantı Linki</label>
                      <a href={registration.meeting_link} target="_blank" rel="noopener noreferrer">
                        {registration.meeting_link}
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Microsoft 365 */}
          <div className="info-card">
            <h2>🔐 Microsoft 365</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Microsoft 365 Var mı?</label>
                <span>{registration.has_microsoft365 ? '✅ Evet' : '❌ Hayır'}</span>
              </div>
              {registration.microsoft_tenant_id && (
                <div className="info-item full-width">
                  <label>Tenant ID</label>
                  <span className="code">{registration.microsoft_tenant_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="sidebar-column">
          {/* Status Timeline */}
          <div className="info-card">
            <h2>📊 Durum</h2>
            <div className="status-timeline">
              <div className="timeline-item active">
                <div className="timeline-icon">📝</div>
                <div className="timeline-content">
                  <strong>Form Gönderildi</strong>
                  <span>{formatDate(registration.created_at)}</span>
                </div>
              </div>
              {registration.approved_at && (
                <div className="timeline-item active">
                  <div className="timeline-icon">✅</div>
                  <div className="timeline-content">
                    <strong>Onaylandı</strong>
                    <span>{formatDate(registration.approved_at)}</span>
                    {registration.approved_by_email && (
                      <small>Onaylayan: {registration.approved_by_email}</small>
                    )}
                  </div>
                </div>
              )}
              {registration.admin_user && (
                <div className="timeline-item active">
                  <div className="timeline-icon">👤</div>
                  <div className="timeline-content">
                    <strong>Hesap Oluşturuldu</strong>
                    <span>{formatDate(registration.updated_at)}</span>
                  </div>
                </div>
              )}
              {registration.scheduled_date && (
                <div className="timeline-item active">
                  <div className="timeline-icon">📅</div>
                  <div className="timeline-content">
                    <strong>Randevu Planlandı</strong>
                    <span>{formatDate(registration.scheduled_date)}</span>
                  </div>
                </div>
              )}
              {registration.status === 'setup_completed' && (
                <div className="timeline-item active">
                  <div className="timeline-icon">🎉</div>
                  <div className="timeline-content">
                    <strong>Kurulum Tamamlandı</strong>
                    <span>{formatDate(registration.completed_at)}</span>
                  </div>
                </div>
              )}
              {registration.status === 'rejected' && (
                <div className="timeline-item rejected">
                  <div className="timeline-icon">❌</div>
                  <div className="timeline-content">
                    <strong>Reddedildi</strong>
                    <span>{formatDate(registration.updated_at)}</span>
                    {registration.rejected_reason && (
                      <small className="reject-reason">{registration.rejected_reason}</small>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          {registration.admin_notes && (
            <div className="info-card">
              <h2>📝 Admin Notları</h2>
              <p className="admin-notes">{registration.admin_notes}</p>
            </div>
          )}

          {/* Activity Log */}
          {activityLogs.length > 0 && (
            <div className="info-card">
              <h2>📋 İşlem Geçmişi</h2>
              <div className="activity-log">
                {activityLogs.map((log, index) => (
                  <div key={index} className="log-item">
                    <div className="log-time">{formatDate(log.created_at)}</div>
                    <div className="log-description">{log.description}</div>
                    {log.performed_by_email && (
                      <div className="log-user">👤 {log.performed_by_email}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showApproveModal && (
        <ApproveModal
          onClose={() => setShowApproveModal(false)}
          onApprove={handleApprove}
          registration={registration}
        />
      )}

      {showRejectModal && (
        <RejectModal
          onClose={() => setShowRejectModal(false)}
          onReject={handleReject}
          registration={registration}
        />
      )}

      {showScheduleModal && (
        <ScheduleModal
          onClose={() => setShowScheduleModal(false)}
          onSchedule={handleSchedule}
          registration={registration}
        />
      )}
    </div>
  );
};

// Approve Modal Component
const ApproveModal = ({ onClose, onApprove, registration }) => {
  const [createAccounts, setCreateAccounts] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onApprove(createAccounts, adminNotes);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✅ Kaydı Onayla</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              <strong>{registration.organization_name}</strong> için kayıt onaylanacak.
            </p>
            
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={createAccounts}
                  onChange={(e) => setCreateAccounts(e.target.checked)}
                />
                <span>Organizasyon ve admin hesabını otomatik oluştur</span>
              </label>
              <small>Onay ile birlikte hesaplar oluşturulacak ve kullanıcıya email gönderilecek</small>
            </div>

            <div className="form-group">
              <label>Admin Notları (Opsiyonel)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Onay ile ilgili notlar..."
                rows={3}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn-approve">
              Onayla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reject Modal Component
const RejectModal = ({ onClose, onReject, registration }) => {
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      alert('Lütfen red nedeni belirtin');
      return;
    }
    onReject(reason);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header reject">
          <h2>❌ Kaydı Reddet</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              <strong>{registration.organization_name}</strong> için kayıt reddedilecek.
            </p>
            
            <div className="form-group">
              <label>Red Nedeni <span className="required">*</span></label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Red nedenini belirtin..."
                rows={4}
                required
              />
              <small>Bu bilgi müşteriye email ile iletilecektir</small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn-reject">
              Reddet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Schedule Modal Component
const ScheduleModal = ({ onClose, onSchedule, registration }) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedPreference, setSelectedPreference] = useState(null);

  const selectPreferredDate = (date) => {
    setSelectedPreference(date);
    // Convert to datetime-local format
    const d = new Date(date);
    const formatted = d.toISOString().slice(0, 16);
    setScheduledDate(formatted);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!scheduledDate) {
      alert('Lütfen randevu tarihi seçin');
      return;
    }
    onSchedule(scheduledDate, meetingLink, adminNotes);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Randevu Planla</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              <strong>{registration.organization_name}</strong> için kurulum randevusu planlayın
            </p>

            {/* Preferred Dates */}
            <div className="form-group">
              <label>Müşteri Tercihleri</label>
              <div className="preference-buttons">
                {registration.preferred_date_1 && (
                  <button
                    type="button"
                    className={`btn-preference ${selectedPreference === registration.preferred_date_1 ? 'selected' : ''}`}
                    onClick={() => selectPreferredDate(registration.preferred_date_1)}
                  >
                    <span className="pref-label">Tercih 1</span>
                    <span className="pref-date">
                      {new Date(registration.preferred_date_1).toLocaleString('tr-TR')}
                    </span>
                  </button>
                )}
                {registration.preferred_date_2 && (
                  <button
                    type="button"
                    className={`btn-preference ${selectedPreference === registration.preferred_date_2 ? 'selected' : ''}`}
                    onClick={() => selectPreferredDate(registration.preferred_date_2)}
                  >
                    <span className="pref-label">Tercih 2</span>
                    <span className="pref-date">
                      {new Date(registration.preferred_date_2).toLocaleString('tr-TR')}
                    </span>
                  </button>
                )}
                {registration.preferred_date_3 && (
                  <button
                    type="button"
                    className={`btn-preference ${selectedPreference === registration.preferred_date_3 ? 'selected' : ''}`}
                    onClick={() => selectPreferredDate(registration.preferred_date_3)}
                  >
                    <span className="pref-label">Tercih 3</span>
                    <span className="pref-date">
                      {new Date(registration.preferred_date_3).toLocaleString('tr-TR')}
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Planlanan Tarih <span className="required">*</span></label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
              />
              <small>Veya müşteri tercihlerinden birini seçebilirsiniz</small>
            </div>

            <div className="form-group">
              <label>Toplantı Linki (Opsiyonel)</label>
              <input
                type="url"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://meet.google.com/... veya https://teams.microsoft.com/..."
              />
              <small>Google Meet, Zoom veya Teams linki ekleyebilirsiniz</small>
            </div>

            <div className="form-group">
              <label>Admin Notları (Opsiyonel)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Randevu ile ilgili notlar..."
                rows={3}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn-schedule">
              Randevuyu Planla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationDetailPage;
